/**
 * Zod Schemas for Observability Events
 *
 * Provides runtime validation schemas for AI observability events.
 * These schemas enable validation of event data before logging.
 *
 * @module schemas/observability-events.zod
 * @see SPEC-015 Requirements 7.1-7.6, 8.1-8.5, 9.1-9.5
 */
import { z } from 'zod';
import { MAX_SUGGEST_CANDIDATES } from '../types/observability-events';
/**
 * Schema for base event fields shared by all observability events.
 *
 * @see Requirements 6.2, 6.3, 6.4, 6.5
 */
export const BaseEventFieldsSchema = z.object({
    requestId: z.string().min(1, 'requestId is required'),
    traceId: z.string().min(1, 'traceId is required'),
    tenantId: z.string().min(1, 'tenantId is required'),
    userIdHash: z.string().min(1, 'userIdHash is required'),
});
/**
 * Schema for score breakdown.
 * All scores should be in 0-1 range (normalized).
 */
export const ScoreBreakdownSchema = z.object({
    vec01: z.number().min(0).max(1),
    graph01: z.number().min(0).max(1),
    time01: z.number().min(0).max(1),
    feedback01: z.number().min(0).max(1),
});
/**
 * Schema for candidate kind.
 */
export const CandidateKindSchema = z.enum(['backlink', 'tag']);
/**
 * Schema for suggest candidate.
 *
 * @see Requirement 7.5
 */
export const SuggestCandidateSchema = z.object({
    candidateId: z.string().min(1),
    kind: CandidateKindSchema,
    finalScore: z.number(),
    scoreBreakdown: ScoreBreakdownSchema,
    bestQueryId: z.enum(['overview', 'recent']).optional(),
    suppressed: z.boolean().optional(),
});
/**
 * Schema for AI suggest latency breakdown.
 * All values in milliseconds.
 *
 * @see Requirement 7.4
 */
export const AiSuggestLatencySchema = z.object({
    total: z.number().nonnegative(),
    loadDoc: z.number().nonnegative(),
    buildQueries: z.number().nonnegative(),
    vectorQuery: z.number().nonnegative(),
    graphFetch: z.number().nonnegative(),
    scoring: z.number().nonnegative(),
});
/**
 * Schema for AI apply latency breakdown.
 * All values in milliseconds.
 *
 * @see Requirement 9.5
 */
export const AiApplyLatencySchema = z.object({
    total: z.number().nonnegative(),
    patchCompute: z.number().nonnegative(),
    writeDoc: z.number().nonnegative(),
    enqueueJobs: z.number().nonnegative(),
});
/**
 * Schema for degrade reasons.
 *
 * - `vector_down`: Vector service is temporarily unavailable
 * - `graph_empty`: No graph edges available for the document
 * - `quota`: User has exceeded their quota
 * - `other`: Other unspecified reason
 * - `vector_not_configured`: Vector service is not configured (SPEC-013)
 * - `vector_connection_failed`: Vector service connection failed (SPEC-013)
 * - `insufficient_docs`: Not enough documents for meaningful suggestions (SPEC-013)
 *
 * @see Requirement 7.6
 * @see SPEC-013 P1 Scenario 1, 2
 */
export const DegradeReasonSchema = z.enum([
    'vector_down',
    'graph_empty',
    'quota',
    'other',
    'vector_not_configured',
    'vector_connection_failed',
    'insufficient_docs',
]);
/**
 * Schema for feedback action.
 *
 * @see Requirement 8.4
 */
export const FeedbackActionSchema = z.enum(['up', 'down']);
/**
 * Schema for apply selection.
 *
 * @see Requirement 9.3
 */
export const ApplySelectionSchema = z.object({
    backlinks: z.array(z.string()),
    tags: z.array(z.string()),
});
/**
 * Schema for AI Suggest Event.
 *
 * @see Requirements 7.1-7.6
 */
export const AiSuggestEventSchema = BaseEventFieldsSchema.extend({
    event: z.literal('ai_suggest'),
    docId: z.string().min(1),
    proposalId: z.string().min(1),
    docContentHash: z.string().min(1),
    modelVersion: z.string().min(1),
    rankingVersion: z.string().min(1),
    latencyMs: AiSuggestLatencySchema,
    candidates: z.array(SuggestCandidateSchema).max(MAX_SUGGEST_CANDIDATES),
    degraded: z.boolean(),
    degradeReason: DegradeReasonSchema.optional(),
});
/**
 * Schema for AI Feedback Event.
 *
 * @see Requirements 8.1-8.5
 */
export const AiFeedbackEventSchema = BaseEventFieldsSchema.extend({
    event: z.literal('ai_feedback'),
    docId: z.string().min(1),
    proposalId: z.string().min(1),
    candidateId: z.string().min(1),
    kind: CandidateKindSchema,
    action: FeedbackActionSchema,
    createdAtMs: z.number().nonnegative(),
});
/**
 * Schema for AI Apply Event.
 *
 * @see Requirements 9.1-9.5
 */
export const AiApplyEventSchema = BaseEventFieldsSchema.extend({
    event: z.literal('ai_apply'),
    docId: z.string().min(1),
    proposalId: z.string().min(1),
    selection: ApplySelectionSchema,
    dryRun: z.boolean(),
    wroteAiFields: z.boolean(),
    latencyMs: AiApplyLatencySchema,
});
/**
 * Discriminated union schema for all observability events.
 */
export const ObservabilityEventSchema = z.discriminatedUnion('event', [
    AiSuggestEventSchema,
    AiFeedbackEventSchema,
    AiApplyEventSchema,
]);
//# sourceMappingURL=observability-events.zod.js.map